/**
 * Funções utilitárias para manipular a estrutura `speakers` / `sentences` da
 * transcrição. Mantidas em um único módulo para facilitar testes futuros e
 * para que a lógica não vaze para os componentes de UI.
 */

const SPEAKER_ID_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

/**
 * Detecta se o `name` é um placeholder do tipo `Speaker N` gerado pela
 * primeira passagem da transcrição, situação em que devemos exibir uma label
 * mais amigável (ex.: "Não identificado") em vez do nome cru.
 *
 * @param {string|null|undefined} name
 * @returns {boolean}
 */
export function isPlaceholderSpeakerName(name) {
  if (!name) return true;
  return /^\s*speaker\s*\d+\s*$/i.test(String(name));
}

/**
 * Devolve o nome a apresentar para um speaker. Se o nome real estiver vazio
 * ou for um placeholder, devolve a `unidentifiedLabel` (texto traduzido).
 *
 * @param {{name?:string}} speaker
 * @param {string} unidentifiedLabel
 * @returns {string}
 */
export function getDisplayName(speaker, unidentifiedLabel) {
  if (!speaker) return unidentifiedLabel;
  const raw = (speaker.name || "").trim();
  if (!raw || isPlaceholderSpeakerName(raw)) return unidentifiedLabel;
  return raw;
}

/**
 * Devolve o `role` (papel) a exibir, ou `null` se não houver.
 *
 * @param {{role?:string}} speaker
 * @returns {string|null}
 */
export function getDisplayRole(speaker) {
  const raw = (speaker?.role || "").trim();
  return raw || null;
}

/**
 * Gera um speaker_id alfanumérico curto (default 6 chars), evitando colisões
 * com os ids já existentes. O backend trata o id como string opaca; usamos
 * o mesmo formato dos exemplos da documentação para manter consistência.
 *
 * @param {{speaker_id?:string}[]} existingSpeakers
 * @param {number} [length=6]
 * @returns {string}
 */
export function generateSpeakerId(existingSpeakers, length = 6) {
  const taken = new Set(
    (existingSpeakers || [])
      .map((s) => s?.speaker_id)
      .filter((v) => typeof v === "string" && v.length > 0),
  );
  for (let attempt = 0; attempt < 256; attempt += 1) {
    let id = "";
    for (let i = 0; i < length; i += 1) {
      id += SPEAKER_ID_ALPHABET.charAt(
        Math.floor(Math.random() * SPEAKER_ID_ALPHABET.length),
      );
    }
    if (!taken.has(id)) return id;
  }
  /* fallback determinístico em caso (impossível) de 256 colisões */
  return `S${Date.now().toString(36).slice(-5)}`;
}

/**
 * Devolve a quantidade de sentenças associadas a um determinado speaker.
 *
 * @param {string} speakerId
 * @param {{speaker_id?:string}[]} sentences
 * @returns {number}
 */
export function countSentencesForSpeaker(speakerId, sentences) {
  if (!speakerId || !Array.isArray(sentences)) return 0;
  return sentences.reduce(
    (acc, s) => (s?.speaker_id === speakerId ? acc + 1 : acc),
    0,
  );
}

/**
 * Mescla um conjunto de speakers em um único speaker:
 *   - Remove da lista todos os ids em `mergeIds`, exceto o `targetId`.
 *   - Atualiza todas as sentenças cujo `speaker_id` esteja em `mergeIds` (ou
 *     seja igual ao `targetId`) para apontar para `targetId`.
 *   - Se `targetUpdates` for fornecido, sobrescreve `name`/`role` do speaker
 *     resultante.
 *
 * @param {object} transcription  conteúdo `{speakers, sentences}` atual
 * @param {string[]} mergeIds     ids a serem mesclados (inclui o target)
 * @param {string} targetId       id do speaker que sobrevive
 * @param {{name?:string,role?:string}} [targetUpdates]
 * @returns {object} novo conteúdo
 */
export function mergeSpeakers(transcription, mergeIds, targetId, targetUpdates) {
  const ids = new Set((mergeIds || []).filter(Boolean));
  if (!ids.has(targetId)) ids.add(targetId);

  const speakers = (transcription?.speakers || [])
    .map((sp) => {
      if (sp?.speaker_id !== targetId) return sp;
      return {
        ...sp,
        ...(targetUpdates?.name != null ? { name: targetUpdates.name } : {}),
        ...(targetUpdates?.role !== undefined
          ? { role: targetUpdates.role }
          : {}),
      };
    })
    .filter((sp) => !ids.has(sp?.speaker_id) || sp?.speaker_id === targetId);

  const sentences = (transcription?.sentences || []).map((s) => {
    if (ids.has(s?.speaker_id) && s?.speaker_id !== targetId) {
      return { ...s, speaker_id: targetId };
    }
    return s;
  });

  return { ...transcription, speakers, sentences };
}

/**
 * Atualiza nome/papel de um speaker (mantendo `speaker_id` e demais campos).
 *
 * @param {object} transcription
 * @param {string} speakerId
 * @param {{name?:string, role?:string}} updates
 * @returns {object} novo conteúdo
 */
export function updateSpeaker(transcription, speakerId, updates) {
  const speakers = (transcription?.speakers || []).map((sp) =>
    sp?.speaker_id === speakerId
      ? {
          ...sp,
          ...(updates.name != null ? { name: updates.name } : {}),
          ...(updates.role !== undefined ? { role: updates.role } : {}),
        }
      : sp,
  );
  return { ...transcription, speakers };
}

/**
 * Adiciona um novo speaker no fim da lista (preservando os slots existentes —
 * e portanto as cores). Devolve também o speaker_id gerado, para que a UI
 * possa selecionar o novo speaker imediatamente.
 *
 * @param {object} transcription
 * @param {{name?:string,role?:string}} payload
 * @returns {{ transcription:object, speakerId:string }}
 */
export function addSpeaker(transcription, payload) {
  const speakerId = generateSpeakerId(transcription?.speakers || []);
  const speakers = [
    ...(transcription?.speakers || []),
    {
      speaker_id: speakerId,
      name: payload?.name || "",
      role: payload?.role || "",
    },
  ];
  return { transcription: { ...transcription, speakers }, speakerId };
}

/**
 * Mantém as sentenças em ordem temporal crescente (o backend espera
 * `sentences` ordenadas). Usado depois de operações de split/edit de tempo.
 *
 * @param {object} transcription
 * @returns {object}
 */
export function sortSentences(transcription) {
  const sentences = [...(transcription?.sentences || [])].sort((a, b) => {
    const sa = Number.isFinite(a?.start) ? a.start : 0;
    const sb = Number.isFinite(b?.start) ? b.start : 0;
    return sa - sb;
  });
  return { ...transcription, sentences };
}

/**
 * Compara dois conteúdos de transcrição para detectar alterações não salvas.
 * Usa serialização normalizada (apenas os campos relevantes), para evitar
 * falsos positivos por diferença de ordem de chaves.
 *
 * @param {object} a
 * @param {object} b
 * @returns {boolean}
 */
export function hasUnsavedChanges(a, b) {
  if (a === b) return false;
  if (!a || !b) return true;
  const norm = (t) => ({
    speakers: (t.speakers || []).map((s) => ({
      speaker_id: s.speaker_id,
      name: s.name || "",
      role: s.role || "",
    })),
    sentences: (t.sentences || []).map((s) => ({
      speaker_id: s.speaker_id,
      text: s.text || "",
      start: Number(s.start) || 0,
      end: Number(s.end) || 0,
      confidence:
        s.confidence == null ? null : Number.parseFloat(Number(s.confidence).toFixed(4)),
    })),
  });
  return JSON.stringify(norm(a)) !== JSON.stringify(norm(b));
}
